import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import media from '../util/media';

const HEADING_COLOR = '#111';
const MUTED = '#6b7280';
const BORDER = 'rgba(0,0,0,0.08)';

const Container = styled.section`
  margin: 0 auto;
  padding: 2rem 0;
  line-height: 1.6;
  background: transparent;
`;

const Heading = styled.h3`
  margin: 0 0 1rem 0;
  color: ${HEADING_COLOR};
`;

const Note = styled.p`
  color: ${MUTED};
  margin: 0 0 1rem 0;
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  border-top: 1px solid ${BORDER};
`;

const Item = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${BORDER};

  small {
    color: ${MUTED};
    font-size: 0.9rem;
    white-space: nowrap;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const PostLink = styled.a`
  color: ${HEADING_COLOR};
  text-decoration: none;
  font-weight: 500;
  position: relative;

  &:hover {
    text-decoration: underline;
  }
`;

const Arrow = styled.span`
  margin-left: 0.35rem;
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 120ms ease, transform 120ms ease;
  display: inline-block;

  ${PostLink}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Responsive = styled.div`
  ${media.phone`
    ${Container} {
      padding-top: 1.25rem;
    }
  `}
`;

export default function RecentPosts({ endpoint, max = 5 }) {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(endpoint);
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (mounted) setPosts((Array.isArray(data) ? data.slice(0, max) : []).map(normalize));
      } catch {
        if (mounted) setPosts([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [endpoint, max]);

  if (posts === null || posts.length === 0) return null;

  return (
    <Container aria-labelledby="recent-posts-heading">
      <Responsive>
        <Heading id="recent-posts-heading">Recent writing</Heading>

        <List>
          {posts.map(p => (
            <Item key={p.url || p.title}>
              <PostLink href={p.url} title={p.title}>
                {p.title}
                <Arrow>→</Arrow>
              </PostLink>
              <small>
                <time dateTime={p.publishedAt}>
                  {formatDate(p.publishedAt)}
                </time>
              </small>
            </Item>
          ))}
        </List>
      </Responsive>
    </Container>
  );
}

function normalize(item) {
  return {
    title: item.title || 'Untitled',
    url: item.url || item.link || '#',
    publishedAt: item.publishedAt || item.pubDate || null,
  };
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}
